import type { SmartFormConfig } from '@pesto/ui/features/SmartForm/types';

import { useMutationWithToast } from '@pesto/ui';
import { CurrencyField } from '@pesto/ui/components/Forms/CurrencyField';
import { Typography } from '@pesto/ui/components/Typography';
import { getCurrentDate } from '@pesto/utils';
import { isValid } from 'date-fns';
import { parseISO } from 'date-fns/parseISO';
import { z } from 'zod';

import type { LoanModalPayload, ModalProps } from '../types/modal.types';

import { usePostExternalRepaymentMutation } from '../../__generated__/graphql/api';
import { getLoanRefetchQuery } from '../../components/LoanDetails/getLoanRefetchQuery';
import { createFormComponent } from '../utils/create-form-component';

const DEFAULT_TITLE = 'External repayment settlement';

const schema = z.object({
  amountCents: z.number().min(1, 'Amount is required'),
  paymentRef: z.string().min(1, 'Payment reference is required'),
  effectiveDate: z
    .string()
    .min(1, 'Effective date is required')
    .refine((val) => isValid(parseISO(val)), {
      message: 'Invalid date format',
    }),
  statementDate: z
    .string()
    .optional()
    .refine((val) => !val || isValid(parseISO(val)), {
      message: 'Invalid date format',
    }),
  title: z.string().optional(),
});

type PostExternalRepaymentForm = z.infer<typeof schema>;

export const PostExternalRepaymentComponent = ({ payload, api }: ModalProps<LoanModalPayload>) => {
  const { loanId } = payload;
  const { handleMutation } = useMutationWithToast();
  const [postExternalRepayment, { loading }] = usePostExternalRepaymentMutation(getLoanRefetchQuery(loanId));

  const handleSubmit = (data: PostExternalRepaymentForm) => {
    void handleMutation({
      mutation: () =>
        postExternalRepayment({
          variables: {
            loanId,
            amountCents: data.amountCents,
            paymentRef: data.paymentRef,
            effectiveDate: data.effectiveDate,
            statementDate: data.statementDate || undefined,
            title: data.title || undefined,
          },
        }),
      extractSuccess: (result) => !!result?.data?.postExternalRepayment,
      onSuccess: () => api.close(),
      debugLabel: 'post-external-repayment',
      toast: {
        success: 'External repayment posted successfully',
        error: 'Failed to post external repayment',
        loading: 'Posting external repayment...',
      },
    });
  };

  const config: SmartFormConfig<PostExternalRepaymentForm> = {
    fieldGroups: [
      {
        name: 'required',
        fields: [
          {
            name: 'amountCents',
            label: 'Amount',
            placeholder: 'Enter amount',
            required: true,
            defaultValue: 0,
            component: CurrencyField,
            componentProps: {
              showCurrencySymbol: true,
            },
          },
          {
            name: 'paymentRef',
            label: 'Payment Reference',
            placeholder: 'e.g. wire reference',
            type: 'text',
            required: true,
            helperText: 'Idempotency key — reposting with identical details is a no-op.',
          },
          {
            name: 'effectiveDate',
            label: 'Effective Date',
            type: 'date',
            required: true,
            helperText: 'Date the external payment was applied.',
          },
        ],
      },
      {
        name: 'optional',
        label: 'Optional',
        description: 'Each falls back to a sensible default when left blank.',
        fields: [
          {
            name: 'statementDate',
            label: 'Statement Date',
            type: 'date',
            required: false,
            helperText: 'Statement period this repayment posts against. Defaults to the effective date.',
          },
          {
            name: 'title',
            label: 'Title',
            placeholder: DEFAULT_TITLE,
            type: 'text',
            required: false,
            helperText: `Label shown on the transaction. Defaults to "${DEFAULT_TITLE}".`,
          },
        ],
      },
    ],
    schema,
    defaultValues: {
      amountCents: 0,
      paymentRef: '',
      effectiveDate: getCurrentDate(),
      statementDate: '',
      title: '',
    },
    submitLabel: 'Post Repayment',
  };

  return (
    <div className="flex flex-col gap-4">
      <Typography variant="body" className="text-muted-foreground text-sm">
        Records a repayment that settled outside Strado&apos;s ACH rails (e.g. a wire from the partner) and runs the
        standard allocation waterfall.
      </Typography>
      {createFormComponent({
        config,
        handleSubmit,
        loading,
      })}
    </div>
  );
};
